import React, { useEffect, useMemo, useState } from 'react'
import { PubMedArticle } from './types'
import { importSelectedPubMedArticles, checkForDuplicates, DuplicateInfo, buildPubMedQuery } from './PubMedUtilities'
import { buildPubMedExportWorkbookData } from './helpers/pubmedExportRows'
import { exportPubMedResultsToExcel } from './helpers/pubmedExcelExport'
import { useFetchAllPubMedArticles } from '../../../../shared/queries/useFetchAllPubMedArticles'
import BatchDraftReviewDialog, { BatchDraftResult } from '../../BatchDraftReviewDialog'
import { sortEnrichedArticles } from './helpers/pubmedEnrichment'
import type { EnrichedPubMedArticle, SortMode } from './helpers/pubmedEnrichment'
import type { PubMedSearchParams } from './types'
import {
    IconButton,
    Box,
    Tooltip,
    Typography,
    Button,
    SvgIcon,
    CircularProgress,
    Select,
    MenuItem,
} from '@mui/material'
import SearchModal from '../../../../shared/components/SearchModal'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import PubMedIcon from '../../../../assets/images/pubmed.png'
import { ReactComponent as DownloadIcon } from '../../../../assets/images/Download_Icon.svg'
import ArticleCard from './ArticleCard'
import PubMedSearch from './PubMedSearch'
import { arysanaLogoPPT } from '../../../../assets/encodedImages'
import store from '../../../../redux/store'
import { useAuth0 } from '../../../../contexts/auth0-context'
import callSecureAPI from '../../../../API/callSecureAPI'
import endpoints from '../../../../API/endpoints'
import useEventLogger from '../../../../shared/hooks/useEventLogger'

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50]

interface PubMedViewProps {
    onImported?: () => void
    trigger?: (handleOpen: () => void) => React.ReactNode
    initialDiseaseArea?: string
    initialInterventions?: string
}

const PubMedView: React.FC<PubMedViewProps> = ({ onImported, trigger, initialDiseaseArea, initialInterventions }) => {
    const { getTokenSilently } = useAuth0()
    const { logEvent, ACTIONS } = useEventLogger()
    const [open, setOpen] = useState(false)
    const [batchReviewOpen, setBatchReviewOpen] = useState(false)
    const [importedDrafts, setImportedDrafts] = useState<BatchDraftResult[]>([])
    const [isImporting, setIsImporting] = useState(false)

    const [selectedArticles, setSelectedArticles] = useState<{ [pmid: string]: EnrichedPubMedArticle }>({})
    const [hasSearched, setHasSearched] = useState(false)
    const [isSearching, setIsSearching] = useState(false)
    const [organization, setOrganization] = useState<any>({})

    // Pagination state: allIds holds the full PMID list from esearch; page is 0-indexed
    const [allIds, setAllIds] = useState<string[]>([])
    const [currentSearchParams, setCurrentSearchParams] = useState<PubMedSearchParams | null>(null)
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)

    // Duplicate detection: map of pmid → duplicate info, populated after each page fetch
    const [duplicateMap, setDuplicateMap] = useState<Record<string, DuplicateInfo>>({})

    // NEW - the two sort modes for ranking
    const [sortMode, setSortMode] = useState<SortMode>('searchTerms')

    // Fetch + enrich (score) the FULL candidate set for the current search up front, so
    // relevance sorting can rank across all results instead of just the currently-loaded page.
    const { data: fetchAllResult, isLoading: isFetchingArticles } = useFetchAllPubMedArticles(
        allIds,
        currentSearchParams
    )
    const enrichedAllArticles = fetchAllResult?.articles ?? []
    const failedArticleIds = fetchAllResult?.failedChunkIds ?? []

    // Cheap: re-sorting on sortMode toggle is just a re-sort of the already-fetched full array,
    // no refetch.
    const sortedAllArticles = useMemo(
        () => sortEnrichedArticles(enrichedAllArticles, sortMode),
        [enrichedAllArticles, sortMode]
    )
    const totalPages = Math.ceil(sortedAllArticles.length / pageSize)

    const articles = useMemo<EnrichedPubMedArticle[]>(
        () => sortedAllArticles.slice(page * pageSize, (page + 1) * pageSize),
        [sortedAllArticles, page, pageSize]
    )

    // Fire duplicate check whenever the visible articles change
    useEffect(() => {
        if (!articles.length) return
        const articleRefs = articles.map((a) => ({ pmid: a.pmid, title: a.title }))
        getTokenSilently({ audience: 'https://atlas.aesara.com' })
            .then((token: string) => checkForDuplicates(articleRefs, token))
            .then(setDuplicateMap)
            .catch((err: any) => console.warn('[Duplicates] Check failed:', err.message))
    }, [articles])

    useEffect(() => {
        async function getOrganization() {
            try {
                const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })

                const org = await callSecureAPI(
                    endpoints.getOrganizationById.url + store.getState().strategy.organizationId,
                    endpoints.getOrganizationById.method,
                    token
                )

                if (org?.success) {
                    setOrganization(org.data)
                }
            } catch (error) {
                console.error('Failed to load organization for export branding:', error)
            }
        }

        getOrganization()
    }, [getTokenSilently])

    const handleOpen = () => setOpen(true)
    const handleClose = () => {
        setOpen(false)
    }

    const handleSearchComplete = (ids: string[], params: PubMedSearchParams) => {
        setAllIds(ids)
        setCurrentSearchParams(params)
        setPage(0)
        setHasSearched(true)

        logEvent(ACTIONS.PUBMED_SEARCH, {
            type: 'PubMed Search',
            data: { name: 'PubMed Literature Search' },
            resultCount: ids.length,
            retmax: params.retmax,
        })
    }

    const handleNextPage = () => {
        if (page < totalPages - 1) setPage((p) => p + 1)
    }

    const handlePrevPage = () => {
        if (page > 0) setPage((p) => p - 1)
    }

    const hasSelection = Object.keys(selectedArticles).length > 0
    const selectedArticleList = Object.values(selectedArticles)

    const handleExportExcel = async () => {
        try {
            let exportArticles: EnrichedPubMedArticle[] = []
            let exportDuplicateMap: Record<string, DuplicateInfo> = duplicateMap

            if (hasSelection) {
                exportArticles = selectedArticleList
            } else {
                const exportData = await fetchAllArticlesForExport()
                exportArticles = exportData.allArticles
                exportDuplicateMap = exportData.allDuplicateMap
            }

            if (!exportArticles.length) return

            const queryString = currentSearchParams ? buildPubMedQuery(currentSearchParams) : ''
            const workbookData = buildPubMedExportWorkbookData(exportArticles, exportDuplicateMap, {
                params: currentSearchParams,
                queryString,
                generatedAt: new Date().toLocaleString(),
                companyName: 'Arysana',
                orgName: organization?.name || '',
                orgLogoData: organization?.lightModeImage || '',
                arysanaLogoData: arysanaLogoPPT,
            })

            const filename = hasSelection ? 'pubmed_selected.xlsx' : 'pubmed_results.xlsx'
            await exportPubMedResultsToExcel(workbookData, filename)

            logEvent(ACTIONS.EXCEL_EXPORT, {
                type: 'PubMed Export',
                data: { name: 'PubMed Literature Review (Excel)' },
                reportType: 'PUBMED_LITERATURE_REVIEW',
                articleCount: exportArticles.length,
                selectedOnly: hasSelection,
            })
        } catch (error) {
            console.error('Failed to export Excel:', error)
            alert('Error exporting Excel file. Check console for details.')
        }
    }

    const fetchAllArticlesForExport = async (): Promise<{
        allArticles: EnrichedPubMedArticle[]
        allDuplicateMap: Record<string, DuplicateInfo>
    }> => {
        if (!sortedAllArticles.length) {
            return { allArticles: [], allDuplicateMap: {} }
        }

        let allDuplicateMap: Record<string, DuplicateInfo> = {}
        try {
            const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
            const articleRefs = sortedAllArticles.map((a) => ({ pmid: a.pmid, title: a.title }))
            allDuplicateMap = await checkForDuplicates(articleRefs, token)
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err)
            console.warn('[Duplicates] Export-wide check failed:', message)
        }

        return {
            allArticles: sortedAllArticles,
            allDuplicateMap,
        }
    }

    const handleExportJson = async () => {
        try {
            let exportArticles: EnrichedPubMedArticle[] = []

            if (hasSelection) {
                exportArticles = selectedArticleList
            } else {
                const exportData = await fetchAllArticlesForExport()
                exportArticles = exportData.allArticles
            }

            if (!exportArticles.length) return

            const json = JSON.stringify(exportArticles, null, 2)
            const blob = new Blob([json], { type: 'application/json' })
            const url = URL.createObjectURL(blob)

            const a = document.createElement('a')
            a.href = url
            a.download = hasSelection ? 'pubmed_selected.json' : 'pubmed_results.json'
            document.body.appendChild(a)
            a.click()
            a.remove()

            URL.revokeObjectURL(url)

            logEvent(ACTIONS.JSON_EXPORT, {
                type: 'PubMed Export',
                data: { name: 'PubMed Results (JSON)' },
                reportType: 'PUBMED_SEARCH_RESULTS',
                articleCount: exportArticles.length,
                selectedOnly: hasSelection,
            })
        } catch (error) {
            console.error('Failed to export JSON:', error)
            alert('Error exporting JSON file. Check console for details.')
        }
    }

    const handleAddArticles = async () => {
        setIsImporting(true)
        try {
            const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
            const imported = await importSelectedPubMedArticles(Object.values(selectedArticles), token)

            logEvent(ACTIONS.PUBMED_EVIDENCE_IMPORT, {
                type: 'PubMed Import',
                data: { name: 'PubMed Evidence Import' },
                selectedCount: Object.keys(selectedArticles).length,
                importedCount: imported.length,
            })

            const drafts: BatchDraftResult[] = imported.map(({ draftId, article }) => ({
                draftId,
                file: null,
                ai: {
                    title: article.title,
                    abstract: article.abstract,
                    year: article.year,
                    authors: article.authors,
                    citation: article.longCitation || article.shortCitation || article.journal || '',
                    shortCitation: article.shortCitation || '',
                    longCitation: article.longCitation || '',
                    url: article.doi ? `https://doi.org/${article.doi}` : '',
                    PMID: article.pmid,
                    category: '---',
                    uniqueIdentifier: article.doi || article.pmid || '',
                },
                editedValues: null,
            }))
            setImportedDrafts(drafts)
            setSelectedArticles({})
            handleClose()
            setBatchReviewOpen(true)
        } catch (error) {
            console.error('Failed to import articles:', error)
            alert('Error importing articles. Check console for details.')
        } finally {
            setIsImporting(false)
        }
    }

    const handleClearSelected = () => {
        setSelectedArticles({})
    }

    const handleSelect = (pmid: string, checked: boolean) => {
        const article = articles.find((a) => a.pmid === pmid)
        setSelectedArticles((prev) => {
            if (!checked) {
                const next = { ...prev }
                delete next[pmid]
                return next
            }
            if (!article) return prev
            return { ...prev, [pmid]: article }
        })
    }

    return (
        <>
            {trigger ? (
                trigger(handleOpen)
            ) : (
                <Tooltip title="Import from PubMed" arrow>
                    <IconButton onClick={handleOpen} sx={{ borderRadius: 3, p: 0.5 }}>
                        <Box
                            component="img"
                            src={PubMedIcon}
                            alt="PubMed import"
                            sx={{ width: 32, height: 32, display: 'block' }}
                        />
                    </IconButton>
                </Tooltip>
            )}

            <SearchModal
                open={open}
                onClose={handleClose}
                title="Import from PubMed"
                leftPanel={
                    <PubMedSearch
                        onSearchComplete={handleSearchComplete}
                        onLoadingChange={setIsSearching}
                        disabled={isFetchingArticles}
                        initialDiseaseArea={initialDiseaseArea}
                        initialInterventions={initialInterventions}
                    />
                }
            >
                <Box sx={{ pt: 0, pb: 2, position: 'relative' }}>
                    {isSearching || isFetchingArticles ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                gap: 2,
                            }}
                        >
                            <CircularProgress />
                            <Typography variant="body2" color="text.secondary">
                                {isSearching ? 'Searching PubMed…' : 'Fetching and ranking articles…'}
                            </Typography>
                            {!isSearching && allIds.length > 50 && (
                                <Typography variant="caption" color="text.secondary">
                                    Ranking {allIds.length} results — this can take a few seconds.
                                </Typography>
                            )}
                        </Box>
                    ) : (
                        <>
                            {articles.length > 0 && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 1,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Per page:
                                        </Typography>
                                        <Select
                                            size="small"
                                            value={pageSize}
                                            onChange={(e) => {
                                                setPageSize(Number(e.target.value))
                                                setPage(0)
                                            }}
                                            sx={{ minWidth: 70 }}
                                        >
                                            {PAGE_SIZE_OPTIONS.map((n) => (
                                                <MenuItem key={n} value={n}>
                                                    {n}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            onClick={handleExportExcel}
                                            color="primary"
                                            variant="outlined"
                                            size="small"
                                            startIcon={
                                                <SvgIcon component={DownloadIcon} inheritViewBox fontSize="small" />
                                            }
                                        >
                                            Export Excel
                                        </Button>
                                        <Button
                                            onClick={handleExportJson}
                                            color="primary"
                                            variant="outlined"
                                            size="small"
                                            startIcon={
                                                <SvgIcon component={DownloadIcon} inheritViewBox fontSize="small" />
                                            }
                                        >
                                            Export JSON
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                            {failedArticleIds.length > 0 && (
                                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1 }}>
                                    {failedArticleIds.length} article(s) could not be retrieved and are excluded from
                                    results.
                                </Typography>
                            )}
                            {totalPages > 1 && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1,
                                        mb: 1,
                                    }}
                                >
                                    <IconButton size="small" onClick={handlePrevPage} disabled={page === 0}>
                                        <ChevronLeft />
                                    </IconButton>
                                    <Typography variant="body2">
                                        Page {page + 1} of {totalPages} ({sortedAllArticles.length} results)
                                    </Typography>
                                    <IconButton size="small" onClick={handleNextPage} disabled={page >= totalPages - 1}>
                                        <ChevronRight />
                                    </IconButton>
                                </Box>
                            )}
                            {articles.length > 0 ? (
                                articles.map((a) => (
                                    <ArticleCard
                                        key={a.pmid}
                                        pmid={a.pmid}
                                        pmcId={a.pmcId}
                                        doi={a.doi}
                                        title={a.title}
                                        authors={Array.isArray(a.authors) ? a.authors : [a.authors]}
                                        authorDetails={a.authorDetails}
                                        affiliations={a.affiliations}
                                        journal={a.journal}
                                        year={a.year}
                                        publicationDate={a.publicationDate}
                                        pages={a.pages}
                                        abstract={a.abstract}
                                        shortCitation={a.shortCitation}
                                        longCitation={a.longCitation}
                                        publicationTypes={a.publicationTypes}
                                        industryAffiliations={a.industryAffiliations}
                                        otherAffiliations={a.otherAffiliations}
                                        interventionMatches={a.interventionMatches}
                                        studyGeographyLabels={a.studyGeographyLabels}
                                        // chemicals={a.chemicals}
                                        hasFreePMC={a.hasFreePMC}
                                        selected={!!selectedArticles[a.pmid]}
                                        onSelect={handleSelect}
                                        keywordHits={a.keywordHits}
                                        matchedKeywordFamilies={a.matchedKeywordFamilies}
                                        searchTermsScore={a.searchTermsScore}
                                        customKeywordsScore={a.customKeywordsScore}
                                        duplicateInfo={duplicateMap[a.pmid]}
                                    />
                                ))
                            ) : (
                                <p>{hasSearched ? 'No results for your search.' : 'No articles yet. Run a search.'}</p>
                            )}
                            {articles.length > 0 && totalPages > 1 && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1,
                                        mt: 1,
                                    }}
                                >
                                    <IconButton size="small" onClick={handlePrevPage} disabled={page === 0}>
                                        <ChevronLeft />
                                    </IconButton>
                                    <Typography variant="body2">
                                        Page {page + 1} of {totalPages} ({sortedAllArticles.length} results)
                                    </Typography>
                                    <IconButton size="small" onClick={handleNextPage} disabled={page >= totalPages - 1}>
                                        <ChevronRight />
                                    </IconButton>
                                </Box>
                            )}
                            {hasSelection && (
                                <Box
                                    sx={{
                                        position: 'sticky',
                                        bottom: 0,
                                        display: 'inline-flex',
                                        p: '12px 16px',
                                        justifyContent: 'flex-end',
                                        alignItems: 'center',
                                        gap: '16px',
                                        borderRadius: '16px',
                                        backgroundColor: 'figma.grayscale.tier7',
                                        boxShadow: '0 8px 16px 0 rgba(0, 0, 0, 0.17)',
                                        float: 'right',
                                        zIndex: 10,
                                    }}
                                >
                                    <Button
                                        onClick={handleClearSelected}
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                    >
                                        Clear Selected
                                    </Button>
                                    <Button
                                        onClick={handleAddArticles}
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        disabled={isImporting}
                                    >
                                        {isImporting ? 'Importing…' : 'Add to Atlas'}
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </SearchModal>

            <BatchDraftReviewDialog
                open={batchReviewOpen}
                onClose={() => setBatchReviewOpen(false)}
                initialResults={importedDrafts}
                onSaved={onImported}
            />
        </>
    )
}

export default PubMedView
